import { Description, List, PlaylistAdd, Search as SearchIcon } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import ReorderIcon from '@mui/icons-material/Reorder';
import { Button, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalV2 from '/@/components/bayer/modal-v2';
import BayerTable, { type BayerTableColumn } from '/@/components/bayer/table';
import settings from '/@/core/settings';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useGrowers } from '/@/hooks/growers';
import { useAdvisorRvLinks, useOwnUserNode, useUserNodes } from '/@/hooks/nodes';
import { useHasWriteAccess, useIsValidatedAdvisor, useIsValidatedSquad, useValidatedType } from '/@/hooks/session';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { getSessionCountry } from '/@/store/session/selectors';
import { getCrops } from '/@/views/dashboard/hooks/crops';
import type { GrowersTabType } from '../../..';
import CreateRecommendationModal from '../../../rv/components/create-recommendation-modal';
import CampaignGrowerRow from './tabs/campaign/grower-row';
import RecommendationsGrowerRow from './tabs/recommendations/grower-row';
import ReportsGrowerRow from './tabs/reports/grower-row';

export const FIELDS_EMPTY_STATE = {
    default: {
        'current-campaign': {
            title: _('Agregá a los primeros productores'),
            subtitle: _('Vas a poder hacer un seguimiento de sus lotes y generar recomendaciones personalizadas.'),
        },
        'past-reports': {
            title: _('Sin reportes de campaña disponibles'),
            subtitle: _(
                'Cuando los productores finalicen sus campañas, vas a poder ver acá un resumen de sus resultados.'
            ),
        },
        recommendations: {
            title: _('No hay recomendaciones generadas.'),
            subtitle: _('Acá vas a ver las recomendaciones generadas durante la campaña.'),
        },
    },
    grower: {
        recommendations: {
            title: _('Generá la primera recomendación'),
            subtitle: _('Ayudá a los productores a optimizar sus decisiones agronómicas.'),
        },
    },
};

const GrowersList = ({
    userNodeId,
    onAddGrower,
    setNewFieldGrowersModalOpen,
    setNewFieldModalGrowerId,
    tab,
    year,
    orderBy: orderByProp,
    order: orderProp,
    forceEmptyState = false,
}: {
    userNodeId: number;
    onAddGrower?: () => void;
    setNewFieldGrowersModalOpen?: (open: boolean) => void;
    setNewFieldModalGrowerId?: (growerId: number | undefined) => void;
    tab: GrowersTabType;
    year?: number;
    orderBy?: string;
    order?: 'asc' | 'desc';
    forceEmptyState?: boolean;
}) => {
    const [newRecommendationModalOpen, setNewRecommendationModalOpen] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [deletedGrowerName, setDeletedGrowerName] = useState<string>('');
    const country = useSelector(getSessionCountry);
    const isAdvisor = useIsValidatedAdvisor();

    const handleGrowerDeleteSuccess = useCallback((growerName: string) => {
        setDeletedGrowerName(growerName);
        setSuccessModalOpen(true);
    }, []);

    // Table Management
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 1000);

    // current campaign filters
    const [cropFilter, setCropFilter] = useState('');
    const [fieldsFilter, setFieldsFilter] = useState('');
    const [segmentFilter, setSegmentFilter] = useState<'common' | 'premium' | ''>('');
    const [advisorFilter, setAdvisorFilter] = useState('');
    // past reports filters
    const [cropFilterPastReports, setCropFilterPastReports] = useState('');

    // order by
    const [orderBy, setOrderBy] = useState(orderByProp || 'name');
    const [order, setOrder] = useState(orderProp || 'asc');
    const isValidatedSquad = useIsValidatedSquad();
    const RVAdvisorLinksQuery = useAdvisorRvLinks({ enabled: !isAdvisor });
    const advisorOptions = useMemo(
        () => [
            { value: '', label: _('Todos') },
            ...(RVAdvisorLinksQuery.data?.results || []).map((link) => ({
                value: link.advisor.id.toString(),
                label: link.advisor.name,
            })),
        ],
        [RVAdvisorLinksQuery.data]
    );

    const atLeastOneGrowerExistsQuery = useUserNodes({
        node: userNodeId,
        page: 1,
        page_size: 1,
        type: 'grower',
    });

    // Table Queries
    const growersListQuery = useGrowers({
        page,
        pageSize,
        node: userNodeId,
        search: debouncedSearch || undefined,
        cmCrop: cropFilter || undefined,
        hasReports: Boolean(cropFilterPastReports) || undefined,
        hasFields: fieldsFilter || undefined,
        country: country.code,
        nodeType: 'grower',
        nodeSubtype: segmentFilter || undefined,
        parentIn: advisorFilter ? [Number(advisorFilter)] : undefined,
        year: year || undefined,
        ordering: orderBy ? `${order === 'asc' ? '' : '-'}${orderBy}` : undefined,
        excludeTargetNode: false,
    });

    const growers = useMemo(() => {
        return growersListQuery.data?.results || [];
    }, [growersListQuery.data]);
    const userType = useValidatedType();

    const canEditResource = useHasWriteAccess();
    const ownUserNode = useOwnUserNode();

    const emptyState = useMemo(() => {
        const isFiltering = search || cropFilter || fieldsFilter || segmentFilter || advisorFilter || undefined;
        let emptyIcon: React.ReactNode;
        let emptyTitle = '';
        let emptySubtitle = '';
        let emptyButton: React.ReactNode;
        const stateForUserType =
            FIELDS_EMPTY_STATE[userType as keyof typeof FIELDS_EMPTY_STATE] || FIELDS_EMPTY_STATE.default;

        if (tab === 'current-campaign') {
            if (stateForUserType && 'current-campaign' in stateForUserType) {
                emptyTitle = stateForUserType['current-campaign'].title;
                emptySubtitle = stateForUserType['current-campaign'].subtitle;
                // Creating a grower is allowed for RV/Advisor/Consultant, never Squad.
                if (canEditResource) {
                    emptyButton = (
                        <Button variant='outlined' color='secondary' size='medium' onClick={onAddGrower}>
                            {_('Agregar productor')}
                        </Button>
                    );
                }
            }
            emptyIcon = <PersonIcon color='primary' sx={{ width: 32, height: 32 }} />;
        } else if (tab === 'past-reports') {
            emptyIcon = <Description color='primary' sx={{ width: 32, height: 32 }} />;
            if (canEditResource) {
                emptyTitle = _('Sin reportes de campaña disponibles');
                emptySubtitle = _(
                    'Cuando los productores que agregaste finalicen sus campañas, vas a poder ver acá un resumen de sus resultados.'
                );
                const isOwn = ownUserNode.data?.id === userNodeId;
                if (isOwn) {
                    emptySubtitle = _('Al finalizar las campañas, vas a poder ver acá un resumen de tus resultados.');
                }
                if (year !== new Date().getFullYear()) {
                    emptyTitle = _('No se encontraron resultados');
                    emptySubtitle = '';
                }
            } else {
                if (stateForUserType && 'past-reports' in stateForUserType) {
                    emptyTitle = stateForUserType['past-reports'].title;
                    emptySubtitle = stateForUserType['past-reports'].subtitle;
                }
            }
        } else if (tab === 'recommendations') {
            if (canEditResource) {
                if (year === new Date().getFullYear()) {
                    emptyIcon = <PlaylistAdd color='primary' sx={{ width: 32, height: 32 }} />;
                    emptyTitle = stateForUserType.recommendations.title;
                    emptySubtitle = stateForUserType.recommendations.subtitle;

                    if ((atLeastOneGrowerExistsQuery.data?.count || 0) > 0) {
                        emptyButton = (
                            <Button
                                variant='contained'
                                color='secondary'
                                onClick={() => setNewRecommendationModalOpen(true)}
                            >
                                {_('Nueva recomendación')}
                            </Button>
                        );
                    }
                } else {
                    emptyIcon = <List color='primary' sx={{ width: 32, height: 32 }} />;
                    emptyTitle = _('No hay recomendaciones generadas este año.');
                }
            } else {
                if (stateForUserType && 'recommendations' in stateForUserType) {
                    emptyTitle = stateForUserType.recommendations.title;
                    emptySubtitle = stateForUserType.recommendations.subtitle;
                    emptyIcon = <ReorderIcon color='primary' sx={{ width: 32, height: 32 }} />;
                }
            }
        }

        if (isFiltering) {
            emptyTitle = _('No se encontraron resultados');
            emptySubtitle = '';
            emptyButton = undefined;
        }

        return {
            title: emptyTitle,
            subtitle: emptySubtitle,
            icon: emptyIcon,
            button: emptyButton,
        };
    }, [
        search,
        cropFilter,
        fieldsFilter,
        segmentFilter,
        advisorFilter,
        tab,
        year,
        canEditResource,
        userType,
        atLeastOneGrowerExistsQuery.data?.count,
    ]);

    // Tabs Management
    const tabs: Record<GrowersTabType, { columns: BayerTableColumn[] }> = {
        'current-campaign': {
            columns: [
                { label: _('Productor'), value: 'name', sortable: true, sortKey: 'name', sortOrder: 'asc' },
                isAdvisor ? { label: _('Segmento'), value: 'segment' } : { label: _('Asesor'), value: 'advisor' },
                { label: _('Lotes totales'), value: 'fields', sortable: true, sortKey: 'fields', sortOrder: 'asc' },
                { label: _('Distribución de Cultivos'), value: 'cropDistribution' },
                ...(isValidatedSquad
                    ? [{ label: _('Informes de visita'), value: 'visitReports' }]
                    : [{ label: _('Sugerencias'), value: 'suggestions' }]),
                { label: '', value: 'actions' },
            ],
        },
        'past-reports': {
            columns: [
                { label: _('Nombre'), value: 'name', sortable: true, sortKey: 'name', sortOrder: 'asc' },
                { label: _('Lotes totales'), value: 'fields', sortable: true, sortKey: 'fields', sortOrder: 'asc' },
                {
                    label: _('Reportes generados'),
                    value: 'reports',
                    sortable: true,
                    sortKey: 'reports',
                },
                { label: _('Rendimiento promedio por cultivo'), value: 'meanYieldPerCrop' },
                { label: '', value: 'actions' },
            ],
        },
        recommendations: {
            columns: [
                { label: _('Nombre'), value: 'name', sortable: true, sortKey: 'name', sortOrder: 'asc' },
                { label: _('Lotes con recomendación'), value: 'withRecommendation' },
                {
                    label: _('Recomendación híbridos'),
                    value: 'withHybridRecommendations',
                    sortable: true,
                    sortKey: 'recommendations',
                    sortOrder: 'desc',
                },
                {
                    label: _('Recomendación Protección'),
                    value: 'withProtectionRecommendation',
                    sortable: true,
                    sortKey: 'recommendations',
                    sortOrder: 'desc',
                },
                { label: '', value: 'actions' },
            ],
        },
    };
    const TableRows = useMemo(() => {
        return growers.map((grower, index) => {
            if (tab === 'current-campaign') {
                return (
                    <CampaignGrowerRow
                        key={grower.id}
                        grower={grower}
                        cropFilter={cropFilter}
                        setNewFieldGrowersModalOpen={setNewFieldGrowersModalOpen}
                        setNewFieldModalGrowerId={setNewFieldModalGrowerId}
                        onGrowerDeleteSuccess={handleGrowerDeleteSuccess}
                    />
                );
            }
            if (tab === 'past-reports') {
                return (
                    <ReportsGrowerRow
                        key={grower.id}
                        grower={grower}
                        cropFilter={cropFilterPastReports}
                        year={year!}
                        onGrowerDeleteSuccess={handleGrowerDeleteSuccess}
                    />
                );
            }
            if (tab === 'recommendations') {
                return (
                    <RecommendationsGrowerRow
                        key={grower.id}
                        grower={grower}
                        onGrowerDeleteSuccess={handleGrowerDeleteSuccess}
                        dataTestId={`growers-list-recommendations-row-${index}`}
                    />
                );
            }
            return null;
        });
    }, [growers, cropFilter, cropFilterPastReports, tab, year, handleGrowerDeleteSuccess]);

    const crops = useMemo(() => getCrops(), []);
    const isFiltersDisabled = growersListQuery.isValidating || !atLeastOneGrowerExistsQuery.data?.count;
    const Filters = useMemo(() => {
        if (tab === 'current-campaign')
            return (
                <>
                    <Grid2 xs={6} md={3}>
                        <MuiSelect
                            inputLabel={_('Cultivos')}
                            value={cropFilter}
                            onChange={(e) => setCropFilter(e.target.value as string)}
                            options={[
                                { value: '', label: _('Todos') },
                                ...Object.values(crops).map((crop) => ({ value: crop.value, label: crop.name })),
                            ]}
                            formControlProps={{
                                fullWidth: true,
                            }}
                            clearable
                            disabled={isFiltersDisabled}
                        />
                    </Grid2>
                    <Grid2 xs={6} md={3}>
                        <MuiSelect
                            inputLabel={_('Lotes')}
                            value={fieldsFilter}
                            onChange={(e) => setFieldsFilter(e.target.value as string)}
                            options={[
                                { value: '', label: _('Todos') },
                                { value: 'true', label: _('Con lotes') },
                                { value: 'false', label: _('Sin lotes') },
                            ]}
                            formControlProps={{
                                fullWidth: true,
                            }}
                            clearable
                            disabled={isFiltersDisabled}
                        />
                    </Grid2>
                    {isAdvisor ? (
                        <Grid2 xs={6} md={3}>
                            <MuiSelect
                                inputLabel={_('Segmento')}
                                value={segmentFilter}
                                onChange={(e) => setSegmentFilter(e.target.value as 'common' | 'premium' | '')}
                                options={[
                                    { value: '', label: _('Todos') },
                                    { value: 'premium', label: _('Premium') },
                                    { value: 'common', label: _('Otros segmentos') },
                                ]}
                                formControlProps={{
                                    fullWidth: true,
                                }}
                                clearable
                                disabled={isFiltersDisabled}
                            />
                        </Grid2>
                    ) : (
                        <Grid2 xs={6} md={3} className='mp-mask'>
                            <MuiSelect
                                inputLabel={_('Asesor')}
                                value={advisorFilter}
                                onChange={(e) => setAdvisorFilter(e.target.value as string)}
                                options={advisorOptions}
                                formControlProps={{
                                    fullWidth: true,
                                }}
                                clearable
                                disabled={isFiltersDisabled}
                            />
                        </Grid2>
                    )}
                </>
            );
        if (tab === 'past-reports')
            return (
                <>
                    <Grid2 xs={6} md={3}>
                        <MuiSelect
                            inputLabel={_('Cultivos')}
                            value={cropFilterPastReports}
                            onChange={(e) => setCropFilterPastReports(e.target.value as string)}
                            options={[
                                { value: '', label: _('Todos') },
                                ...Object.values(crops).map((crop) => ({ value: crop.value, label: crop.name })),
                            ]}
                            formControlProps={{
                                fullWidth: true,
                            }}
                            clearable
                        />
                    </Grid2>
                    {/* <Grid2 xs={6} md={3}>
                        <MuiSelect
                            inputLabel={_('Tipo de siembra')}
                            value={fieldsFilter}
                            onChange={(e) => setFieldsFilter(e.target.value as string)}
                            options={[{ value: '', label: _('Todos') }]} // TODO: Add options based on Dashboard API?
                            formControlProps={{
                                fullWidth: true,
                            }}
                            clearable
                            disabled={cropFilter === ''}
                        />
                    </Grid2> */}
                </>
            );
        return null;
    }, [
        cropFilter,
        fieldsFilter,
        segmentFilter,
        advisorFilter,
        advisorOptions,
        cropFilterPastReports,
        tab,
        isAdvisor,
        crops,
        isFiltersDisabled,
    ]);

    return (
        <Stack spacing={3}>
            {/* Search and Filters */}
            <Grid2 container spacing={2} mx={'-8px !important'}>
                <Grid2 xs={12} md>
                    <TextField
                        label={_('Buscar un productor por nombre')}
                        disabled={isFiltersDisabled}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        fullWidth
                    />
                </Grid2>
                {Filters}
            </Grid2>
            {/* Table */}
            <div className='mp-mask'>
                <BayerTable
                    columns={tabs[tab].columns}
                    rows={TableRows}
                    page={page}
                    pageSize={pageSize}
                    setPage={setPage}
                    setPageSize={setPageSize}
                    orderBy={orderBy}
                    order={order}
                    setOrderBy={setOrderBy}
                    setOrder={setOrder}
                    isValidating={growersListQuery.isLoading}
                    count={growersListQuery.data?.count}
                    emptyState={emptyState}
                    forceEmptyState={forceEmptyState}
                />
            </div>

            <CreateRecommendationModal
                open={newRecommendationModalOpen}
                setOpen={setNewRecommendationModalOpen}
                entrySource='growers-list'
            />

            {/* Success Modal for Grower Deletion */}
            <ModalV2
                open={successModalOpen}
                setOpen={setSuccessModalOpen}
                onClose={() => setSuccessModalOpen(false)}
                title={_('Productor eliminado')}
                actions={
                    <Button variant='contained' color='secondary' onClick={() => setSuccessModalOpen(false)}>
                        {_('Volver a mis productores')}
                    </Button>
                }
                actionsProps={{
                    justifyContent: 'end',
                }}
            >
                <Typography variant='body1' color='primary'>
                    {_('{{growerName}} ya no aparece en tu lista de productores en {{appName}}.', {
                        growerName: deletedGrowerName,
                        appName: settings.app.name,
                    })}
                </Typography>
            </ModalV2>
        </Stack>
    );
};

export default GrowersList;
